import React from 'react';
import { Icon, Text } from '@goodfynd/ui';

import styles from './GoodfyndSitesContactInfo.module.css';
import type { GoodfyndSitesContactInfoProps } from './types';

export function GoodfyndSitesContactInfo({
  contact,
  socialLinks,
}: GoodfyndSitesContactInfoProps) {
  const hasEmails = contact.emails && contact.emails.length > 0;
  const hasPhones = contact.phoneNumbers && contact.phoneNumbers.length > 0;
  const hasContactHours = contact.contactHours && contact.contactHours.trim();
  const hasOtherUrls =
    socialLinks?.otherUrls && Object.keys(socialLinks.otherUrls).length > 0;
  const hasSocialLinks =
    socialLinks &&
    (socialLinks.facebook ||
      socialLinks.instagram ||
      socialLinks.x ||
      socialLinks.website ||
      hasOtherUrls);

  const hasAnyContact =
    hasEmails || hasPhones || hasContactHours || hasSocialLinks;

  if (!hasAnyContact) {
    return (
      <Text className={styles['empty-message']}>
        Contact information coming soon. Check back later!
      </Text>
    );
  }

  return (
    <div className={styles['contact-container']}>
      {hasEmails && (
        <div className={styles['contact-section']}>
          <div className={styles['section-header']}>
            <div className={styles['section-icon']}>
              <Icon name="email" />
            </div>
            <Text as="subhead2">Email</Text>
          </div>
          <ul className={styles['contact-list']}>
            {contact.emails.map((email) => (
              <li className={styles['contact-item']} key={email}>
                <a href={`mailto:${email}`}>{email}</a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasPhones && (
        <div className={styles['contact-section']}>
          <div className={styles['section-header']}>
            <div className={styles['section-icon']}>
              <Icon name="numpad" />
            </div>
            <Text as="subhead2">Phone</Text>
          </div>
          <ul className={styles['contact-list']}>
            {contact.phoneNumbers.map((phone) => (
              <li className={styles['contact-item']} key={phone}>
                <a href={`tel:${phone}`}>{phone}</a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasContactHours && (
        <div className={styles['contact-section']}>
          <div className={styles['section-header']}>
            <div className={styles['section-icon']}>
              <Icon name="clock" />
            </div>
            <Text as="subhead2">Hours</Text>
          </div>
          <div className={styles['hours-content']}>
            <Text tag="pre">{contact.contactHours}</Text>
          </div>
        </div>
      )}

      {hasSocialLinks && (
        <div className={styles['contact-section']}>
          <div className={styles['section-header']}>
            <div className={styles['section-icon']}>
              <Icon name="share_android" />
            </div>
            <Text as="subhead2">Follow Us</Text>
          </div>

          <div className={styles['social-links']}>
            {socialLinks.facebook && (
              <a
                className={styles['social-link']}
                href={socialLinks.facebook}
                rel="noopener noreferrer"
                target="_blank"
              >
                <span className={styles['social-link-icon']}>
                  <Icon name="facebook" />
                </span>
                <Text>Facebook</Text>
              </a>
            )}

            {socialLinks.instagram && (
              <a
                className={styles['social-link']}
                href={socialLinks.instagram}
                rel="noopener noreferrer"
                target="_blank"
              >
                <span className={styles['social-link-icon']}>
                  <Icon name="instagram" />
                </span>
                <Text>Instagram</Text>
              </a>
            )}
            {socialLinks.x && (
              <a
                className={styles['social-link']}
                href={socialLinks.x}
                rel="noopener noreferrer"
                target="_blank"
              >
                <span className={styles['social-link-icon']}>
                  <Icon name="twitter" />
                </span>
                <Text>Twitter</Text>
              </a>
            )}
            {socialLinks.website && (
              <a
                className={styles['social-link']}
                href={socialLinks.website}
                rel="noopener noreferrer"
                target="_blank"
              >
                <span className={styles['social-link-icon']}>
                  <Icon name="link" />
                </span>
                <Text>Website</Text>
              </a>
            )}
            {socialLinks.otherUrls &&
              Object.entries(socialLinks.otherUrls).map(([label, url]) => (
                <a
                  className={styles['social-link']}
                  href={url}
                  key={label}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <span className={styles['social-link-icon']}>
                    <Icon name="link" />
                  </span>
                  <Text>{label}</Text>
                </a>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
